import React from "react";
import { useTranslation } from "react-i18next";
import { Clock, FileText, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GuardianScoreHeroGauge from "./GuardianScoreHeroGauge";
import { fmtMoney } from "../../lib/money";

/**
 * QENVA · Hero Command Center — the centerpiece of the Dashboard.
 *
 * Layout (per Figma):
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ GUARDIAN ACTIF · 24 documents sous surveillance       [⊙ 87]│
 *   │                                                              │
 *   │ BONJOUR AKLI —                                         /100  │
 *   │                                                              │
 *   │ Votre entreprise est protégée.                       ◯ BON   │
 *   │                                                              │
 *   │ Guardian a identifié X risques critiques et Y opportunités   │
 *   │ représentant Z $ de valeur préservable.       +4 pts ce mois │
 *   │                                                              │
 *   │ EXPOSITION   PRÉSERVÉ   CROSSCHECK™   ALERTES                │
 *   │ 248 400 $    38 200 $   94/100        3 actives              │
 *   └─────────────────────────────────────────────────────────────┘
 */

export default function HeroCommandCenter({ data, lastAnalysis, onAnalyze, onOpenReport, testId = "hero-command-center" }) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();

  const userFirstname = data?.user?.firstname || "Utilisateur";
  const hero = data?.hero || {};
  const score = hero.guardian_score || {};
  const kpis = hero.kpis || {};
  const docs = hero.documents_under_surveillance ?? 0;
  const criticals = hero.critical_risks_count ?? 0;
  const opps = hero.opportunities_count ?? 0;
  const preservable = hero.preservable_value || {};
  const statusActive = (hero.guardian_status || "active") === "active";

  return (
    <section data-testid={testId} className="space-y-5">
      {/* Top bar: title + analyse pill + actions */}
      <header className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 text-zinc-400 text-[13px]" data-testid={`${testId}-context`}>
          <span className="text-qenva-navy/70 font-semibold">Guardian™ Command Center</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-bold ${
              statusActive
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                : "bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200"
            }`}
            data-testid={`${testId}-status`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${statusActive ? "bg-emerald-500" : "bg-zinc-400"}`} />
            {statusActive ? "Guardian Actif" : "Guardian Inactif"}
          </span>
          {lastAnalysis && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] text-zinc-500 bg-zinc-100">
              <Clock size={11} />
              {lastAnalysis}
            </span>
          )}
          <button
            type="button"
            onClick={onOpenReport}
            data-testid={`${testId}-report-btn`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-bold bg-qenva-navy text-white hover:bg-qenva-navy/90 transition-colors"
          >
            <FileText size={13} />
            Rapport Exécutif
          </button>
          <button
            type="button"
            onClick={onAnalyze}
            data-testid={`${testId}-analyse-btn`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-bold bg-qenva-blue text-white hover:bg-qenva-blue/90 transition-colors"
          >
            <RefreshCw size={13} />
            Analyser
          </button>
        </div>
      </header>

      {/* Hero block — deep navy gradient */}
      <div
        className="relative rounded-3xl overflow-hidden bg-qenva-navy text-white p-8 sm:p-10"
        style={{
          background: "linear-gradient(135deg, #04102A 0%, #0B1B3B 55%, #0E2C5E 100%)",
        }}
        data-testid={`${testId}-block`}
      >
        {/* Subtle dot grid texture */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-start">
          {/* Left: copy */}
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap mb-5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 ring-1 ring-emerald-400/30 text-emerald-300 text-[10.5px] font-bold uppercase tracking-[0.16em]" data-testid={`${testId}-active-badge`}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Guardian Actif
              </span>
              <span className="text-white/55 text-[12.5px]" data-testid={`${testId}-docs-count`}>
                {docs} document{docs > 1 ? "s" : ""} sous surveillance
              </span>
            </div>
            <p className="text-[11px] tracking-[0.18em] font-bold uppercase text-qenva-blue mb-3" data-testid={`${testId}-greeting`}>
              Bonjour {userFirstname} —
            </p>
            <h1 className="font-display font-bold text-white text-[clamp(28px,4vw,46px)] leading-[1.05] tracking-tight max-w-2xl" data-testid={`${testId}-title`}>
              Votre entreprise est protégée.
            </h1>
            <p className="text-[14px] text-white/75 max-w-2xl mt-5 leading-relaxed" data-testid={`${testId}-summary`}>
              Guardian a identifié{" "}
              <span className="text-rose-300 font-semibold">
                {criticals} risque{criticals > 1 ? "s" : ""} critique{criticals > 1 ? "s" : ""}
              </span>{" "}
              et{" "}
              <span className="text-amber-300 font-semibold">
                {opps} opportunité{opps > 1 ? "s" : ""} d'optimisation
              </span>{" "}
              représentant{" "}
              <span className="text-emerald-300 font-semibold">
                {fmtMoney(preservable.amount, preservable.currency, { compact: false })}
              </span>{" "}
              de valeur préservable.
            </p>

            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 mt-8" data-testid={`${testId}-kpis`}>
              <Kpi label="Exposition" value={fmtMoney(kpis.exposure?.amount, kpis.exposure?.currency, { compact: false })} tone="white" testId={`${testId}-kpi-exposure`} />
              <Kpi label="Préservé" value={fmtMoney(kpis.preserved?.amount, kpis.preserved?.currency, { compact: false })} tone="success" testId={`${testId}-kpi-preserved`} />
              <Kpi label="CrossCheck™" value={`${kpis.crosscheck_score ?? 0}/100`} tone="info" testId={`${testId}-kpi-crosscheck`} />
              <Kpi label="Alertes" value={`${kpis.active_alerts ?? 0} active${(kpis.active_alerts ?? 0) > 1 ? "s" : ""}`} tone={(kpis.active_alerts ?? 0) > 0 ? "warning" : "neutral"} testId={`${testId}-kpi-alerts`} />
            </div>
          </div>

          {/* Right: Score gauge */}
          <div className="flex-shrink-0">
            <GuardianScoreHeroGauge
              value={score.value || 0}
              band={score.band || "good"}
              delta={score.delta_this_month || 0}
              size={170}
              testId={`${testId}-score-gauge`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Kpi({ label, value, tone, testId }) {
  const valueClass = {
    white: "text-white",
    success: "text-emerald-300",
    info: "text-qenva-blue",
    warning: "text-amber-300",
    neutral: "text-white/80",
  }[tone || "white"];
  return (
    <div data-testid={testId}>
      <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-white/55 mb-1">{label}</p>
      <p className={`font-display font-bold text-[20px] sm:text-[22px] tabular-nums leading-none ${valueClass}`}>{value}</p>
    </div>
  );
}
